import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { API_BASE_URL } from '../config/api';
import { useAuth } from './AuthContext';

const BasketContext = createContext(null);

export const BasketProvider = ({ children }) => {
  const { authToken, isAuthenticated } = useAuth();
  const [baskets, setBaskets] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [loading, setLoading] = useState(false);

  const fetchBaskets = useCallback(async () => {
    if (!isAuthenticated || !authToken) {
      setBaskets([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/baskets`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBaskets(data.data || []);
        if (data.currency?.symbol) {
          setCurrencySymbol(data.currency.symbol);
        }
      }
    } catch (err) {
      console.error('Fetch baskets error:', err);
    }
    setLoading(false);
  }, [authToken, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBaskets();
    } else {
      setBaskets([]);
    }
  }, [isAuthenticated, fetchBaskets]);

  const createBasket = async (name, description) => {
    if (!authToken) return null;

    try {
      const response = await fetch(`${API_BASE_URL}/baskets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, description }),
      });

      if (response.ok) {
        const data = await response.json();
        await fetchBaskets();
        return data.data;
      }
    } catch (err) {
      console.error('Create basket error:', err);
    }
    return null;
  };

  const deleteBasket = async (basketId) => {
    if (!authToken) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/baskets/${basketId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        setBaskets(prev => prev.filter(b => b.id !== basketId));
        return true;
      }
    } catch (err) {
      console.error('Delete basket error:', err);
    }
    return false;
  };

  const addToBasket = async (basketId, productId, quantity = 1) => {
    if (!authToken) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/baskets/${basketId}/items`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ product_id: productId, quantity }),
      });

      if (response.ok) {
        await fetchBaskets();
        return true;
      }
    } catch (err) {
      console.error('Add to basket error:', err);
    }
    return false;
  };

  const removeFromBasket = async (basketId, itemId) => {
    if (!authToken) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/baskets/${basketId}/items/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        await fetchBaskets();
        return true;
      }
    } catch (err) {
      console.error('Remove from basket error:', err);
    }
    return false;
  };

  const updateItemQuantity = async (basketId, itemId, quantity) => {
    if (!authToken) return false;

    try {
      const response = await fetch(`${API_BASE_URL}/baskets/${basketId}/items/${itemId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity }),
      });

      if (response.ok) {
        await fetchBaskets();
        return true;
      }
    } catch (err) {
      console.error('Update item quantity error:', err);
    }
    return false;
  };

  const userBaskets = baskets.filter(b => !b.is_system);
  const systemBaskets = baskets.filter(b => b.is_system);

  return (
    <BasketContext.Provider value={{
      baskets,
      userBaskets,
      systemBaskets,
      currencySymbol,
      loading,
      fetchBaskets,
      createBasket,
      deleteBasket,
      addToBasket,
      removeFromBasket,
      updateItemQuantity,
    }}>
      {children}
    </BasketContext.Provider>
  );
};

export const useBaskets = () => {
  return useContext(BasketContext);
};
